import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { 
  ClockIcon, 
  AcademicCapIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  StarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarDaysIcon,
  ArrowLeftIcon,
  PlayIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import { courses } from '../data/courses';
import { courseStructuredData, faqStructuredData } from '../data/seo';

const CourseDetail = () => {
  const { slug } = useParams();
  const [expandedSection, setExpandedSection] = useState(null);
  
  const course = courses.find(c => c.slug === slug);
  
  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Course Not Found</h1>
          <Link to="/courses" className="text-blue-600 dark:text-blue-400 hover:underline">
            ← Back to Courses
          </Link>
        </div>
      </div>
    );
  }

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO
        title={`${course.title} Course - Learn ${course.title} with Expert Mentorship`}
        description={course.detailedDescription}
        keywords={`${course.title.toLowerCase()}, ${course.title.toLowerCase()} course, ${course.title.toLowerCase()} training, ${course.title.toLowerCase()} tutorial, programming course, coding bootcamp`}
        canonical={`/courses/${course.slug}`}
        ogImage={`/og-${course.slug}.jpg`}
        structuredData={[courseStructuredData(course), faqStructuredData(course.faq)]}
      />

      {/* Breadcrumb */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">Home</Link>
            <span className="text-gray-400">/</span>
            <Link to="/courses" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">Courses</Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 dark:text-white font-medium">{course.title}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className={`${course.color} text-white py-16`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center mb-6">
                <Link 
                  to="/courses"
                  className="flex items-center text-white/80 hover:text-white transition-colors duration-200 mr-4"
                >
                  <ArrowLeftIcon className="h-5 w-5 mr-2" />
                  Back to Courses
                </Link>
              </div>
              
              <div className="text-6xl mb-6">{course.icon}</div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">{course.title}</h1>
              <p className="text-xl text-white/90 mb-8">{course.detailedDescription}</p>
              
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 mr-2" />
                  <span>{course.duration}</span>
                </div>
                <div className="flex items-center">
                  <AcademicCapIcon className="h-5 w-5 mr-2" />
                  <span>{course.level}</span>
                </div>
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                  <span>{course.price}</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/booking"
                  className="inline-flex items-center px-6 py-3 bg-white text-gray-900 font-medium rounded-lg hover:bg-gray-100 transition-all duration-200 transform hover:scale-105"
                >
                  <CalendarDaysIcon className="h-5 w-5 mr-2" />
                  Enroll Now
                </Link>
                <button className="inline-flex items-center px-6 py-3 border-2 border-white text-white font-medium rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-200">
                  <PlayIcon className="h-5 w-5 mr-2" />
                  Watch Demo
                </button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <h3 className="text-2xl font-bold mb-6">What You'll Learn</h3>
              <div className="space-y-4">
                {course.outcomes.map((outcome, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="flex items-center"
                  >
                    <CheckCircleIcon className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
                    <span className="text-lg">{outcome}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Course Details */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Curriculum */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="mb-12"
              >
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Course Curriculum</h2>
                <div className="space-y-4">
                  {course.curriculum.map((module, index) => (
                    <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{module.title}</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{module.week}</p>
                        </div>
                        <button
                          onClick={() => toggleSection(`curriculum-${index}`)}
                          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                        >
                          {expandedSection === `curriculum-${index}` ? (
                            <ChevronUpIcon className="h-5 w-5" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {expandedSection === `curriculum-${index}` && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="space-y-2"
                        >
                          {module.topics.map((topic, topicIndex) => (
                            <div key={topicIndex} className="flex items-center text-gray-700 dark:text-gray-300">
                              <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                              {topic}
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Projects */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mb-12"
              >
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Hands-On Projects</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {course.projects.map((project, index) => (
                    <div key={index} className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center mb-3">
                        <RocketLaunchIcon className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Project {index + 1}</h3>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300">{project}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* FAQ */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="mb-12"
              >
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Frequently Asked Questions</h2>
                <div className="space-y-4">
                  {course.faq.map((faq, index) => (
                    <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                      <button
                        onClick={() => toggleSection(`faq-${index}`)}
                        className="w-full text-left flex items-center justify-between"
                      >
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{faq.question}</h3>
                        {expandedSection === `faq-${index}` ? (
                          <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                        )}
                      </button>
                      {expandedSection === `faq-${index}` && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4"
                        >
                          <p className="text-gray-700 dark:text-gray-300">{faq.answer}</p>
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Course Info Card */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8 sticky top-8"
              >
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Course Details</h3>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center">
                    <ClockIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Duration</div>
                      <div className="text-gray-600 dark:text-gray-400">{course.duration}</div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <AcademicCapIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Level</div>
                      <div className="text-gray-600 dark:text-gray-400">{course.level}</div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Price</div>
                      <div className="text-gray-600 dark:text-gray-400">{course.price}</div>
                    </div>
                  </div>
                </div>

                <Link
                  to="/booking"
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 flex items-center justify-center mb-4"
                >
                  <CalendarDaysIcon className="h-5 w-5 mr-2" />
                  Enroll Now
                </Link>

                <div className="text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Start your journey today
                  </p>
                </div>
              </motion.div>

              {/* Prerequisites */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8"
              >
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Prerequisites</h3>
                <div className="space-y-2">
                  {course.prerequisites.map((prereq, index) => (
                    <div key={index} className="flex items-center text-gray-700 dark:text-gray-300">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      {prereq}
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Testimonials */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
              >
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Student Reviews</h3>
                {course.testimonials.map((testimonial, index) => (
                  <div key={index} className="mb-4">
                    <div className="flex items-center mb-2">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <StarIcon key={i} className="h-4 w-4 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">"{testimonial.content}"</p>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{testimonial.name}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">{testimonial.role}</div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold mb-6">
              Ready to Master {course.title}?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Join hundreds of students who have transformed their careers with this comprehensive course. 
              Start your journey today with personalized mentorship and hands-on projects.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/booking"
                className="inline-flex items-center px-6 py-3 bg-white text-blue-600 font-medium rounded-lg hover:bg-gray-100 transition-all duration-200 transform hover:scale-105"
              >
                <CalendarDaysIcon className="h-5 w-5 mr-2" />
                Enroll Now
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 border-2 border-white text-white font-medium rounded-lg hover:bg-white hover:text-blue-600 transition-all duration-200"
              >
                Ask Questions
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CourseDetail;
